import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { apiClient } from "@/api/apiClient";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff, ArrowLeft } from "lucide-react";

import { useCategories } from "@/hooks/useCategories";
import { prepareRegisterPayload } from "./registerHelpers";

const RegisterPage = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const { categories } = useCategories(); // Get current categories (guest or otherwise)
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        brand_name: '',

        password: '',
        confirmPassword: ''
    });

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.id]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.password !== formData.confirmPassword) {
            toast.error("Les mots de passe ne correspondent pas");
            return;
        }

        setIsLoading(true);

        try {
            const payload = prepareRegisterPayload({
                email: formData.email,
                brand_name: formData.brand_name,
                password: formData.password,
            }, categories);

            await apiClient.auth.register(payload);
            // Verify Logic: Remove token to force login after verification
            localStorage.removeItem('token');
            // Remove guest data too just in case
            localStorage.removeItem('guest_id');

            toast.success("Compte créé ! Vérifiez vos emails.");
            navigate('/check-email', { state: { email: formData.email } });
        } catch (error) {
            // console.error(error);
            const errorMessage = error.response?.data?.details?.[0] || error.response?.data?.error || "Erreur lors de l'inscription";
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="h-screen flex items-center justify-center bg-gray-50 dark:bg-[hsl(228,14%,6%)] relative">
            <Button 
                variant="ghost" 
                className="absolute top-4 left-4 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
                onClick={() => navigate('/home')}
            >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Retour au menu
            </Button>
            <Card className="w-[350px]">
                <CardHeader>
                    <CardTitle>Inscription</CardTitle>
                    <CardDescription>Créez votre compte pour commencer</CardDescription>
                </CardHeader>
                <form onSubmit={handleSubmit}>
                    <CardContent>
                        <div className="grid w-full items-center gap-4">
                            <div className="flex flex-col space-y-1.5">
                                <Label htmlFor="email">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="nom@exemple.com"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            <div className="flex flex-col space-y-1.5">
                                <Label htmlFor="brand_name">Pseudo</Label>
                                <Input
                                    id="brand_name"
                                    type="text"
                                    placeholder="Votre pseudo"
                                    value={formData.brand_name}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <div className="flex flex-col space-y-1.5">
                                <Label htmlFor="password">Mot de passe</Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? "text" : "password"}
                                        value={formData.password}
                                        onChange={handleChange}
                                        required
                                        minLength={8}
                                        pattern="^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$"
                                        title="Le mot de passe doit contenir au moins 8 caractères, une majuscule, une minuscule et un chiffre."
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                                    >
                                        {showPassword ? (
                                            <EyeOff className="h-4 w-4" />
                                        ) : (
                                            <Eye className="h-4 w-4" />
                                        )}
                                    </button>
                                </div>
                                <p className="text-[10px] text-gray-500 mt-1">
                                    8+ car., 1 majuscule, 1 minuscule, 1 chiffre.
                                </p>
                            </div>
                            <div className="flex flex-col space-y-1.5">
                                <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                                <div className="relative">
                                    <Input
                                        id="confirmPassword"
                                        type={showConfirmPassword ? "text" : "password"}
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                                    >
                                        {showConfirmPassword ? (
                                            <EyeOff className="h-4 w-4" />
                                        ) : (
                                            <Eye className="h-4 w-4" />
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                    <CardFooter className="flex flex-col gap-4">
                        <Button className="w-full" type="submit" disabled={isLoading}>
                            {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                            S'inscrire
                        </Button>
                        <div className="text-sm text-center text-gray-500">
                            Déjà un compte ? <Link to="/login" className="text-amber-600 dark:text-amber-400 hover:underline">Se connecter</Link>
                        </div>
                    </CardFooter>
                </form>
            </Card>
        </div>
    );
};

export default RegisterPage;
